import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { NATIONAL_PARTNERS, INTL_DESTINATIONS, LINKS } from "../../data/content";
import { NationalBenefitsCarousel } from "../shared/NationalBenefitsCarousel";

const ArrowButton = ({ direction, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    aria-label={direction === "left" ? "Derulează la stânga" : "Derulează la dreapta"}
    className={`hidden md:flex xl:hidden absolute top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border border-zinc-200 shadow-lg text-zinc-700 hover:bg-brand hover:text-white hover:border-brand transition-colors duration-200 ${direction === "left" ? "left-2 lg:left-4" : "right-2 lg:right-4"}`}
  >
    {direction === "left" ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
  </button>
);

const ScrollRow = ({ children, testIdPrefix }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div className="relative">
      <ArrowButton direction="left" onClick={() => scroll(-1)} testId={`${testIdPrefix}-arrow-left`} />
      <div ref={ref} className="flex gap-4 md:gap-6 xl:gap-4 overflow-x-auto xl:overflow-visible xl:justify-center snap-x snap-mandatory xl:snap-none no-scrollbar px-5 md:px-8 pt-3 pb-4">
        {children}
      </div>
      <ArrowButton direction="right" onClick={() => scroll(1)} testId={`${testIdPrefix}-arrow-right`} />
    </div>
  );
};

const SectionHead = ({ title, subtitle }) => (
  <div className="max-w-3xl px-5 md:px-8 mx-auto lg:mx-0 lg:px-0">
    <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight text-zinc-900">{title}</h2>
    <p className="mt-4 text-base md:text-lg text-zinc-600 leading-relaxed">{subtitle}</p>
  </div>
);

export const NationalCarousel = () => (
  <section id="reduceri" className="bg-zinc-50 border-y border-zinc-200 py-16 md:py-24" data-testid="national-carousel-section">
    <div className="max-w-7xl mx-auto lg:px-8">
      <SectionHead
        title="Reduceri în România"
        subtitle="Descoperă beneficii pe care le poți folosi zi de zi, cu cardul tău BRD-ISIC."
      />
    </div>
    <div className="mt-10 max-w-7xl mx-auto px-5 lg:px-8">
      <NationalBenefitsCarousel
        items={NATIONAL_PARTNERS}
        allLink={LINKS.discountsRo}
        testIdPrefix="national"
        cardHoverClass="hover:border-brand/40"
        textClass="text-zinc-800"
        fallbackClass="font-heading font-extrabold text-lg text-zinc-900 text-center"
        buttonClass="font-heading text-zinc-900 hover:border-brand hover:text-brand"
      />
    </div>
  </section>
);

export const IntlCarousel = () => (
  <section className="bg-white py-16 md:py-24" data-testid="intl-carousel-section">
    <div className="max-w-7xl mx-auto lg:px-8">
      <SectionHead
        title="Reduceri internaționale"
        subtitle="Când călătorești sau studiezi în altă țară, BRD-ISIC îți oferă acces la beneficiile ISIC din întreaga lume."
      />
    </div>
    <div className="mt-10 max-w-7xl mx-auto">
      <ScrollRow testIdPrefix="intl">
        {INTL_DESTINATIONS.map((d) => (
          <div
            key={d.name}
            data-testid={`intl-card-${d.name.split(" ")[0].toLowerCase()}`}
            className="snap-start shrink-0 w-[36vw] sm:w-56 md:w-60 xl:w-52 overflow-hidden rounded-2xl hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
          >
            <img src={d.image} alt={d.name} className="w-full h-auto" />
          </div>
        ))}
      </ScrollRow>
    </div>
    <div className="mt-10 text-center">
      <a
        href={LINKS.discountsIntl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="more-intl-discounts-btn"
        className="inline-flex items-center rounded-full border-2 border-zinc-200 px-8 py-3.5 text-base font-semibold text-zinc-800 hover:border-brand hover:text-brand transition-colors duration-300"
      >
        Mai multe reduceri internaționale
      </a>
    </div>
  </section>
);
